import { useState } from 'react';
import { Cloud, GitBranch, KeyRound, Plus } from 'lucide-react';
import {
  BRANCH_API_STATE_LABELS,
  type BusinessDetailsView,
  type CloudPosStatus,
  type OperationalBranch,
} from '@quds/shared';
import { useCan } from '@/auth/AuthContext';
import { Button, EmptyState, StatusBadge } from '@/components/ui';
import { BranchApiManager } from '../api/BranchApiManager';
import { createProductionBranch } from '@/data';
import { errorMessage } from '@/lib/api';

function syncLabel(sync: OperationalBranch['sync']): string {
  if (sync === 'never_synced') return 'Never Synced';
  if (sync === 'unknown') return '—';
  return sync;
}

function syncTone(sync: OperationalBranch['sync']): 'success' | 'warning' | 'neutral' | 'danger' {
  if (sync === 'ONLINE') return 'success';
  if (sync === 'STALE') return 'warning';
  if (sync === 'OFFLINE') return 'danger';
  return 'neutral';
}

function apiTone(state: OperationalBranch['apiState']): 'success' | 'warning' | 'neutral' {
  if (state === 'configured' || state === 'configured_recoverable') return 'success';
  if (state === 'configured_not_recoverable') return 'warning';
  if (state === 'not_configured') return 'neutral';
  return 'neutral';
}

interface Props {
  details: BusinessDetailsView | null;
  businessId: string;
  loading?: boolean;
  onAddBranch: () => void;
  onOpenBranch: (row: OperationalBranch) => void;
  onSetUpCloudPos: () => void;
  onRefresh: () => void;
  focusApiBranchId?: string | null;
}

/**
 * Single Branches experience — composes CA + Production into one list.
 */
export function UnifiedBranchesPanel({
  details,
  businessId,
  loading,
  onAddBranch,
  onOpenBranch,
  onSetUpCloudPos,
  onRefresh,
  focusApiBranchId,
}: Props) {
  const can = useCan();
  const canAdd = can('branch.create');
  const canTenant = can('tenant.manage');
  const canRead = can('branch.read');
  const cloudPos: CloudPosStatus = details?.overview.cloudPosStatus ?? 'not_connected';
  const branches = details?.operationalBranches ?? [];
  const productionAvailable = details?.overview.productionAvailable ?? false;
  const [connectingId, setConnectingId] = useState<string | null>(null);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [apiOpenId, setApiOpenId] = useState<string | null>(focusApiBranchId ?? null);

  const connectBranch = async (row: OperationalBranch) => {
    if (!row.caBranchId || !row.caReferenceCode) return;
    if (cloudPos === 'not_connected') {
      onSetUpCloudPos();
      return;
    }
    setConnectingId(row.id);
    setConnectError(null);
    try {
      await createProductionBranch(businessId, {
        branch: {
          branchRef: row.caReferenceCode,
          name: row.name,
          localBranchId: row.caBranchId,
          primary: false,
        },
        createApiKeyForFirstBranch: false,
      });
      onRefresh();
    } catch (e) {
      setConnectError(errorMessage(e));
    } finally {
      setConnectingId(null);
    }
  };

  if (loading && !details) {
    return <p className="px-5 py-8 text-sm text-ink-muted">Loading branches…</p>;
  }

  return (
    <div className="xl:col-span-2">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
            <GitBranch size={16} aria-hidden="true" />
            Branches
          </h2>
          <p className="mt-0.5 text-xs text-ink-secondary">
            {branches.length === 0
              ? 'No locations yet'
              : `${branches.length} location${branches.length === 1 ? '' : 's'}`}
            {cloudPos === 'not_connected' ? ' · Cloud POS Not Connected' : null}
            {cloudPos === 'partially_connected' ? ' · Cloud POS Partially Connected' : null}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canTenant && cloudPos === 'not_connected' && (
            <Button variant="secondary" size="sm" leftIcon={<Cloud size={14} aria-hidden="true" />} onClick={onSetUpCloudPos}>
              Set Up Cloud POS
            </Button>
          )}
          {canAdd && (
            <Button variant="primary" size="sm" leftIcon={<Plus size={14} aria-hidden="true" />} onClick={onAddBranch}>
              Add Branch
            </Button>
          )}
        </div>
      </div>

      {connectError && (
        <p role="alert" className="mb-3 rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">
          {connectError}
        </p>
      )}

      {branches.length === 0 ? (
        <EmptyState
          title="No branches yet"
          description="Add the first physical pharmacy location for this business."
          action={
            canAdd ? (
              <Button variant="primary" size="sm" leftIcon={<Plus size={14} aria-hidden="true" />} onClick={onAddBranch}>
                Add Branch
              </Button>
            ) : undefined
          }
        />
      ) : (
        <ul className="divide-y divide-line rounded-md border border-line bg-surface">
          {branches.map((row) => {
            const prodBranchId = row.productionBranchId;
            const keys = (details?.api.keys ?? []).filter((k) => k.branchId === prodBranchId);
            const showApi = apiOpenId === row.id || focusApiBranchId === row.id;
            const branchId = row.productionBranchRef ?? row.caReferenceCode ?? '—';
            return (
              <li key={row.id} className="px-4 py-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    {canRead ? (
                      <button
                        type="button"
                        className="text-left text-sm font-medium text-ink hover:text-primary hover:underline"
                        onClick={() => onOpenBranch(row)}
                      >
                        {row.name}
                      </button>
                    ) : (
                      <p className="text-sm font-medium text-ink">{row.name}</p>
                    )}
                    {row.locationLabel && <p className="text-xs text-ink-muted">{row.locationLabel}</p>}
                    <p className="mt-0.5 text-xs text-ink-muted">
                      Branch ID: <span className="font-mono text-ink-secondary">{branchId}</span>
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <StatusBadge
                        tone={row.cloudPos === 'connected' ? 'success' : 'neutral'}
                        label={row.cloudPos === 'connected' ? 'Cloud POS Connected' : 'Cloud POS Not Connected'}
                        dot={false}
                      />
                      <StatusBadge tone={apiTone(row.apiState)} label={`API ${BRANCH_API_STATE_LABELS[row.apiState]}`} dot={false} />
                      {row.cloudPos === 'connected' && (
                        <StatusBadge tone={syncTone(row.sync)} label={`Sync ${syncLabel(row.sync)}`} />
                      )}
                      {row.cloudPos === 'connected' && (
                        <StatusBadge
                          tone={row.posDeviceCount > 0 ? 'success' : 'neutral'}
                          label={row.posDeviceCount > 0 ? 'POS Connected' : 'POS Not Connected'}
                          dot={false}
                        />
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {canRead && (
                      <Button variant="ghost" size="sm" onClick={() => onOpenBranch(row)}>
                        View / Edit
                      </Button>
                    )}
                    {row.cloudPos === 'not_connected' && (canTenant || canAdd) && (
                      <Button
                        variant="secondary"
                        size="sm"
                        loading={connectingId === row.id}
                        leftIcon={<Cloud size={14} aria-hidden="true" />}
                        onClick={() => void connectBranch(row)}
                      >
                        {cloudPos === 'not_connected' ? 'Set Up Cloud POS' : 'Connect to Cloud POS'}
                      </Button>
                    )}
                    {row.cloudPos === 'connected' && prodBranchId && (
                      <Button
                        variant="ghost"
                        size="sm"
                        leftIcon={<KeyRound size={14} aria-hidden="true" />}
                        onClick={() => setApiOpenId((cur) => (cur === row.id ? null : row.id))}
                      >
                        {row.apiState === 'not_configured' ? 'Generate API' : 'API'}
                      </Button>
                    )}
                  </div>
                </div>
                {showApi && prodBranchId && (
                  <div className="mt-3">
                    <BranchApiManager
                      businessId={businessId}
                      branchId={prodBranchId}
                      branchName={row.name}
                      keys={keys}
                      productionAvailable={productionAvailable}
                      onChanged={onRefresh}
                      focused
                    />
                    <p className="mt-2 text-[12px] text-ink-muted">
                      Copy this API into the Point of Sale for this branch. Enrollment is optional and not required for stock sync.
                    </p>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
